import { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { amortizarCompromiso } from "../../utils/compromisos/amortizacionCompromiso";
import { client } from "../../utils/supabase";

// "Amortizar con un pago ya hecho" — kebab de Futuros sobre la fila del compromiso (P1.10
// redefinido, §4.2.3 del diseño, molde AsociarPagosCapexModal). El compromiso ya existe y el
// movimiento egreso YA fue vinculado por su camino real en algún momento — esto NO vincula nada
// nuevo, solo registra la amortización RETROACTIVA vía la misma RPC `amortizar_compromiso` que usa
// el checkbox de VincularMovimientoModal. Errores de la base (saldo máximo embebido, 8.6) llegan tal
// cual al toast, nunca en silencio (10.177).

const fmtMonto = (n, moneda) => {
  const pref = moneda === "USD" ? "US$ " : moneda === "PEN" ? "S/ " : "$";
  return pref + Number(n || 0).toLocaleString("es-CL", { maximumFractionDigits: moneda === "USD" ? 2 : 0 });
};

const AmortizarCompromisoConPagoModal = ({ show, onHide, entry, pais, usuarioId, onAmortizado }) => {
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [movimientos, setMovimientos] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [movimientoId, setMovimientoId] = useState("");
  const [monto, setMonto] = useState("");

  useEffect(() => {
    if (!show || !pais) return;
    let vivo = true;
    (async () => {
      setCargando(true);
      try {
        const { data, error } = await client
          .from("movimientos_bancarios")
          .select("id, fecha, monto, moneda, descripcion_raw")
          .eq("pais", pais)
          .eq("tipo", "egreso")
          .eq("estado", "procesado")
          .order("fecha", { ascending: false });
        if (error) throw error;
        if (vivo) setMovimientos(data || []);
      } catch (e) {
        toast.error(`Error al cargar movimientos: ${e.message || e}`);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [show, pais]);

  useEffect(() => {
    if (!show) {
      setBusqueda("");
      setMovimientoId("");
      setMonto("");
    }
  }, [show]);

  if (!entry) return null;

  const movimientoSel = movimientos.find((m) => String(m.id) === String(movimientoId)) || null;
  const montoNum = Number(monto || 0);
  const puedeGuardar = !guardando && !!movimientoSel && montoNum > 0;

  const q = busqueda.trim().toLowerCase();
  const coincide = (m) =>
    (m.descripcion_raw || "").toLowerCase().includes(q) || String(m.monto).includes(q) || (m.fecha || "").includes(q);
  const movimientosFiltrados = q ? movimientos.filter(coincide) : movimientos;

  const handleGuardar = async () => {
    if (!movimientoSel || montoNum <= 0) return;
    setGuardando(true);
    try {
      const resultado = await amortizarCompromiso({
        movimientoBancarioId: movimientoSel.id,
        compromisoId: entry.compromiso_id,
        monto: montoNum,
        usuarioId,
      });
      toast.success(`Compromiso amortizado por ${fmtMonto(montoNum, entry.moneda)}`);
      onAmortizado?.(resultado);
      onHide?.();
    } catch (e) {
      // 10.177: el motivo exacto de la base (saldo máximo embebido, 8.6) llega tal cual, nunca en silencio.
      toast.error(`Error al amortizar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Amortizar con un pago ya hecho</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ fontSize: "0.9rem", lineHeight: 1.7, marginBottom: 16 }}>
          <div>
            <strong>Compromiso:</strong> {entry.razon_social_receptor || "—"}
          </div>
          <div>
            <strong>Saldo pendiente:</strong> {fmtMonto(entry.saldo_pendiente ?? entry.monto, entry.moneda)}
          </div>
        </div>

        <Form.Group style={{ marginBottom: 12 }}>
          <Form.Label style={{ fontWeight: 600, fontSize: "0.9rem" }}>
            Movimiento bancario (egreso, ya procesado)
          </Form.Label>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Buscar por descripción, monto o fecha..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            style={{ marginBottom: 8 }}
            disabled={cargando}
          />
          {cargando ? (
            <div style={{ color: "#999", fontSize: "0.85rem" }}>Cargando movimientos...</div>
          ) : (
            <Form.Select
              size="sm"
              value={movimientoId}
              onChange={(e) => setMovimientoId(e.target.value)}
              disabled={guardando}
            >
              <option value="">Elegí un movimiento...</option>
              {movimientosFiltrados.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.fecha} · {fmtMonto(m.monto, m.moneda)} · {m.descripcion_raw || "(sin descripción)"}
                </option>
              ))}
            </Form.Select>
          )}
        </Form.Group>

        <Form.Group>
          <Form.Label style={{ fontWeight: 600, fontSize: "0.9rem" }}>
            Monto a amortizar {entry.moneda ? `(${entry.moneda})` : ""}
          </Form.Label>
          <Form.Control
            type="number"
            min={0}
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            disabled={guardando}
            style={montoNum <= 0 ? { borderColor: "#dc3545" } : undefined}
          />
          {/* Sin conversión automática (8.2): el monto se tipea en la moneda del COMPROMISO, aunque
              el movimiento elegido esté en otra — monto_banco/moneda_banco quedan de auditoría. */}
          {movimientoSel && movimientoSel.moneda !== entry.moneda && (
            <Form.Text style={{ color: "#856404" }}>
              El movimiento es en {movimientoSel.moneda} — el monto se registra en {entry.moneda} (moneda del
              compromiso), sin conversión automática.
            </Form.Text>
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide} disabled={guardando}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={!puedeGuardar}>
          {guardando ? "Guardando…" : "Amortizar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AmortizarCompromisoConPagoModal;
